import { useState } from 'react';

import axios from 'axios';
import { get, omit, debounce, isFunction } from 'lodash';

import { useInfiniteQuery } from 'react-query';

import { DEFAULT_PAGE_SIZE } from 'constants/api';
import { debounceInterval as debounceIntervalValue } from 'constants/debounceInterval';
import Api from 'core/api';
import {
  getPaginatedApiDataForDataTable,
  formatOrder,
} from 'selectors/paginatedRestApi';
import addDevelopmentData from 'utils/addDevelopmentData';

import { minLengthString } from 'utils/string';

/**
 * Returns the pagination config for different types of supported pagination types
 * @param {Boolean} useCursorPagination If true, it is using cursor pagination
 * @param {Boolean} useLimitOffset If true, it is using limit/offset pagination
 * @returns {Object} pagination config, containing pageField, pageSizeField and initialPage
 */
const getPaginationConfig = ({ useCursorPagination, useLimitOffset }) => {
  if (useCursorPagination) {
    return { pageField: 'cursor', pageSizeField: 'limit', initialPage: 0 };
  }
  if (useLimitOffset) {
    return { pageField: 'offset', pageSizeField: 'limit', initialPage: 0 };
  }
  return { pageField: 'page', pageSizeField: 'pageSize', initialPage: 1 };
};

/**
 * Use student list hook
 * provide data of array of students
 * provide state management for sorting/ordering
 * provide state of loading
 *
 * @param  {Object} filters                  Formatted filter that is ready to be plugged into any query directly
 * @param  {String} searchTerm               Value that to be added to search query param
 * @param  {Number} pageSize                 Value that to be added to pageSize or limit query param
 * @param  {Boolean} useLimitOffset          If true, it is using limit/offset pagination
 * @param  {Boolean} useCursorPagination     If true, it is using cursor pagination
 * @param  {Function} apiFunc                API function to be called
 * @param  {Boolean} enabled                 If true, the query is enabled
 * @param  {String} defaultOrder             Default order value
 * @param  {String} defaultOrderBy           Default order by value
 * @param  {String} apiFuncName              API function name
 * @param  {String} customQueryKey           An optional custom query key. If none provided, the query key will be [`infiniteScroll.${apiFuncName}`, [filters, searchTerm, searchString, order, orderBy, apiFuncParams]];
 * @param  {String} dataResultsPath          Key that returns results from api response
 * @param  {Function} developmentAdjustFunc  This function is used for adding fake data to your API responses
 * @param  {Function} searchField            Query parameter name used for search functionality
 * @param  {Function} staleTime              Query stale time
 * @param  {Function} select                 This function can be used to transform or select a part of the data
 * @param  {Function} selectItems            This function can be used to transform items after the data was extracted from the pages
 * @param  {Boolean} disableHasNextPage      If true, force hasNextPage to be set to false
 * @param  {String} initialSearchTerm        Initial value for search, used to persist search after navigation
 */
// eslint-disable-next-line complexity
const usePaginatedApi = ({
  filters = {},
  searchTerm = '',
  pageSize = DEFAULT_PAGE_SIZE,
  useLimitOffset = false,
  useCursorPagination = false,
  apiFunc = Api.slUsers.getStudentList,
  enabled = true,
  defaultOrder = '',
  defaultOrderBy = '',
  apiFuncName = 'getStudentList',
  dataPath = 'data',
  dataResultsPath = 'results',
  apiFuncParams = {},
  onSuccess,
  debounceInterval = debounceIntervalValue,
  searchField = 'search',
  developmentAdjustFunc,
  staleTime,
  select,
  selectItems,
  hasPaginationKey = false,
  disableHasNextPage = false,
  initialSearchTerm = '',
} = {}) => {
  const [order, setOrder] = useState(defaultOrder);
  const [orderBy, setOrderBy] = useState(defaultOrderBy);
  const apiFuncToCall = apiFunc || Api.slUsers.getStudentList;
  const { pageField, pageSizeField, initialPage } = getPaginationConfig({
    useCursorPagination,
    useLimitOffset,
  });
  const [searchString, setSearchString] = useState(initialSearchTerm);

  const minLengthSearch = minLengthString({
    str: searchString || searchTerm,
  });

  const listApiQueryKey = [
    `infiniteScroll.${apiFuncName}`,
    [filters, searchTerm, minLengthSearch, order, orderBy, apiFuncParams],
  ];

  const {
    data: listApiData,
    isSuccess,
    isLoading,
    isFetching,
    isFetchingNextPage,
    refetch: fetchInitial,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: listApiQueryKey,
    queryFn: context => {
      const { pageParam: page } = context;
      const { CancelToken } = axios;
      const source = CancelToken.source();

      const nextPage = page || initialPage;

      const params = {
        [pageSizeField]: pageSize,
        ...filters,
        ordering: formatOrder({ order, orderBy }),
        [searchField]: minLengthSearch,
      };

      // For cursor pagination, don't send the `cursor` param for initial request
      if (!(useCursorPagination && nextPage === 0)) {
        params[pageField] = page || initialPage;
      }

      const hasSearch = Boolean(minLengthSearch);

      const promise = apiFuncToCall({
        ...apiFuncParams,
        params: hasSearch ? params : omit(params, searchField),
        cancelToken: source.token,
      });
      promise.cancel = () => {
        source.cancel('Query was cancelled by React Query');
      };
      return promise;
    },
    enabled,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    staleTime,
    select: select
      ? ({ pages }) => select(pages)
      : ({ pages }) =>
          getPaginatedApiDataForDataTable({
            pages,
            dataPath,
            dataResultsPath,
          }),
    getNextPageParam: lastPage => {
      const nextUrl = get(
        lastPage,
        hasPaginationKey ? `${dataPath}.pagination.next` : `${dataPath}.next`,
      );
      if (nextUrl) {
        const params = new URL(nextUrl).searchParams;
        return params.get(pageField);
      }
      return undefined;
    },
    getPreviousPageParam: firstPage => {
      const prevUrl = get(
        firstPage,
        hasPaginationKey ? `${dataPath}.pagination.prev` : `${dataPath}.prev`,
      );
      if (prevUrl) {
        const params = new URL(prevUrl).searchParams;
        return params.get(pageField);
      }
      return undefined;
    },
    onSuccess,
  });

  const debouncedSetSearchString = debounce(setSearchString, debounceInterval);

  const hasMockData = isFunction(developmentAdjustFunc);

  let items = hasMockData
    ? addDevelopmentData({ developmentAdjustFunc })
    : (listApiData?.results ?? []);

  if (isFunction(selectItems)) {
    items = selectItems(items);
  }

  return {
    count: listApiData?.count ?? 0,
    fetchInitial,
    hasNextPage: disableHasNextPage ? false : hasNextPage,
    isLoadingInitial: isLoading,
    isLoading: isFetching,
    isLoadingMore: isFetchingNextPage,
    isRefetching: isFetching && !isLoading, // Is true whenever a background refetch is in-flight, which does not include initial loading
    isSuccess,
    items,
    loadNextPage: fetchNextPage,
    order,
    orderBy,
    searchString,
    setOrder,
    setOrderBy,
    setSearchString: debouncedSetSearchString,
    queryKey: listApiQueryKey,
    listApiData,
  };
};

export default usePaginatedApi;
